import * as signalR from "@microsoft/signalr";
import Swal from "sweetalert2";
import api from "@/assets/Js/api";
import newOrderSound from "@/assets/Alert/new-order.mp3"; // 匯入新訂單音效
import realtimeSyncService from "@/assets/Js/realtime-sync.js";

// SignalR 服務類別
class SignalRService {
  constructor() {
    this.connection = null; // SignalR 連線實例
    this.vueInstance = null; // Vue 組件實例
    this.isConnected = false; // 連線狀態
    this.lastLocalUpdate = 0; // 最後本地操作時間
    this.hasJoinedStoreGroup = false; // 是否已加入後端依 JWT 決定的店家群組
    this.storeGroupRetryTimer = null; // 群組加入暫時失敗時的自動重試計時器
    this.onNewOrderCallback = null; // 新訂單的回呼函式
    this.currentTableDraftId = null; // 目前加入的桌次共享草稿群組
    this.tableDraftChangedCallback = null; // 草稿更新回呼
    this.tableDraftSubmittedCallback = null; // 草稿送廚房回呼
  }

  // 設定 Vue 組件實例
  setVueInstance(vue) {
    this.vueInstance = vue;
  }

  // 啟動 SignalR 連線，並設定監聽事件
  async start(isPos = false) {
    if (this.isConnected) {
      // SignalR 已連線但群組加入可能失敗；再次 start 時自動補加入。
      if (!this.hasJoinedStoreGroup) {
        await this.joinStoreGroup();
      }
      return;
    }

    try {
      const negotiateResponse = await api.post("/negotiate", {});
      const negotiateData = negotiateResponse.data || {};
      const Url = negotiateData.url || negotiateData.Url;
      const AccessToken =
        negotiateData.accessToken || negotiateData.AccessToken;

      if (!Url || !AccessToken) {
        throw new Error("SignalR negotiate 回傳格式錯誤");
      }
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(Url, {
          accessTokenFactory: () => AccessToken,
          transport: signalR.HttpTransportType.LongPolling, // ✅ 強制用 LongPolling 避免 WebSocket 被斷
        })
        .withAutomaticReconnect()
        .build();

      this.setupListeners(isPos); // 設定監聽器

      this.connection.on("receiveMessage", (message) => {
        Swal.fire({
          title: "📨 收到新訊息",
          text: JSON.stringify(message),
          icon: "info",
          confirmButtonText: "確定",
        });
      });


      this.connection.onreconnecting(() => {
        this.isConnected = false;
      });
      this.connection.onreconnected(async () => {
        this.isConnected = true;
        this.hasJoinedStoreGroup = false;
        await this.joinStoreGroup();
        if (this.currentTableDraftId) {
          this.joinTableDraftGroup(this.currentTableDraftId);
        }
        if (this.vueInstance)
          setTimeout(() => this.vueInstance.updateLocalCart(), 500);
      });

      this.connection.onclose(() => {
        this.isConnected = false;
      });

      await this.connection.start();
      this.isConnected = true;
      await this.joinStoreGroup();
      // ✅ 加入心跳維持機制（每 15 秒 Ping 一次）
      this.pingTimer = setInterval(() => {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
          api
            .post("/ping") // ✅ 改為使用已封裝的 api.js 發送 ping 請求
            .then(() => {
              console.log("✅ Ping 成功");
            })
            .catch((e) => {
              console.warn("⚠️ Ping 失敗：", e);
            });
        }
      }, 15000); // 每 15 秒打一次
    } catch (err) {
      this.isConnected = false;
      throw err;
    }
  }

  // 設定 SignalR 事件監聽
  setupListeners(isPos = false) {
    this.connection.on("refreshCart", (data) => {
      this.handleCartUpdate(data);
      realtimeSyncService.requestRefresh("cart-changed", "cart", true, data);
    });
    this.connection.on("storeDataChanged", (data) => {
      realtimeSyncService.requestRefresh(
        data?.action || "store-data-changed",
        data?.resource || "all",
        true,
        data
      );
    });
    this.connection.on("tableDraftChanged", (data) => {
      if (this.tableDraftChangedCallback) {
        this.tableDraftChangedCallback(data);
      }
      window.dispatchEvent(
        new CustomEvent("table-draft-changed", { detail: data })
      );
    });
    this.connection.on("tableDraftSubmitted", (data) => {
      if (this.tableDraftSubmittedCallback) {
        this.tableDraftSubmittedCallback(data);
      }
      window.dispatchEvent(
        new CustomEvent("table-draft-submitted", { detail: data })
      );
    });

    this.connection.on("newOrder", (data) => {
      if (!data) return;
      if (isPos) this.playNotificationSound();
      window.dispatchEvent(
        new CustomEvent("new-order-received", { detail: data })
      );
    });
  }

  // 播放新訂單音效
  playNotificationSound() {
    const audio = new Audio(newOrderSound);
    audio.volume = 1.0;
    audio.play().catch(() => {});
  }

  // 處理購物車更新訊息
  async handleCartUpdate(data) {
    if (!this.vueInstance) return;

    const { displayOrderCode, cartId } = this.vueInstance;
    const isRelevant =
      (data.shareCode && data.shareCode === displayOrderCode) ||
      (data.cartId && data.cartId === cartId);
    if (!isRelevant) return;

    const isLikelyLocalUpdate = Date.now() - this.lastLocalUpdate < 3000;
    if (isLikelyLocalUpdate) {
      this.showSyncNotification(data);
    } else {
      try {
        await this.vueInstance.updateLocalCart();
        this.showUpdateNotification(data);
        //eslint-disable-next-line no-empty
      } catch {}
    }
  }

  // 加入目前 JWT 所屬的店家群組；前端不再知道或傳送 StoreId。
  async joinStoreGroup() {
    if (!this.isConnected || !this.connection) return;

    const connectionId = this.connection.connectionId;
    if (!connectionId) {
      this.scheduleStoreGroupRetry();
      return;
    }

    try {
      await api.post("/addToGroup", { connectionId });
      this.hasJoinedStoreGroup = true;

      if (this.storeGroupRetryTimer) {
        clearTimeout(this.storeGroupRetryTimer);
        this.storeGroupRetryTimer = null;
      }
    } catch (error) {
      this.hasJoinedStoreGroup = false;
      console.warn("加入 SignalR 店家群組失敗，3 秒後自動重試", error);
      this.scheduleStoreGroupRetry();
    }
  }

  // SignalR Connected 不代表已加入 store_{StoreId} 群組；加入失敗時自動修復。
  scheduleStoreGroupRetry() {
    if (this.storeGroupRetryTimer || !this.connection) return;

    this.storeGroupRetryTimer = window.setTimeout(async () => {
      this.storeGroupRetryTimer = null;

      if (this.connection?.state === signalR.HubConnectionState.Connected) {
        this.isConnected = true;
        await this.joinStoreGroup();
      }
    }, 3000);
  }

  // 加入目前桌位的共享草稿群組；群組名稱由後端驗證並產生。
  async joinTableDraftGroup(tableId) {
    const normalizedTableId = Number(tableId);
    if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return;

    if (!this.isConnected || !this.connection) {
      await this.start(true);
    }

    if (
      this.currentTableDraftId &&
      this.currentTableDraftId !== normalizedTableId
    ) {
      await this.leaveTableDraftGroup(this.currentTableDraftId);
    }

    await api.post("/addToTableDraftGroup", {
      connectionId: this.connection.connectionId,
      tableId: normalizedTableId,
    });
    this.currentTableDraftId = normalizedTableId;
  }

  // 離開桌次共享草稿群組，避免切換桌位後仍收到舊桌通知。
  async leaveTableDraftGroup(tableId = this.currentTableDraftId) {
    const normalizedTableId = Number(tableId);
    if (
      !Number.isInteger(normalizedTableId) ||
      normalizedTableId <= 0 ||
      !this.isConnected ||
      !this.connection
    ) {
      this.currentTableDraftId = null;
      return;
    }

    try {
      await api.post("/removeFromTableDraftGroup", {
        connectionId: this.connection.connectionId,
        tableId: normalizedTableId,
      });
    } catch (error) {
      console.warn("離開桌次共享草稿群組失敗", error);
    } finally {
      if (this.currentTableDraftId === normalizedTableId) {
        this.currentTableDraftId = null;
      }
    }
  }

  registerTableDraftCallbacks({ onChanged, onSubmitted } = {}) {
    this.tableDraftChangedCallback =
      typeof onChanged === "function" ? onChanged : null;
    this.tableDraftSubmittedCallback =
      typeof onSubmitted === "function" ? onSubmitted : null;
  }

  unregisterTableDraftCallbacks() {
    this.tableDraftChangedCallback = null;
    this.tableDraftSubmittedCallback = null;
  }

  // 標記最近的本地更新時間
  markLocalUpdate() {
    this.lastLocalUpdate = Date.now();
  }

  // 顯示來自他人操作的購物車更新通知
  showUpdateNotification(data) {
    let title = "👥 其他用戶更新了購物車";
    let text = data.message || "購物車有變化";
    let icon = "info";

    switch (data.type) {
      case "itemAdded":
        title = "➕ 有人加入了商品";
        text = "購物車新增了商品";
        icon = "success";
        break;
      case "itemUpdated":
        title = "🔄 有人修改了數量";
        text = "商品數量已更新";
        icon = "info";
        break;
      case "itemRemoved":
        title = "🗑️ 有人移除了商品";
        text = "商品已被移除";
        icon = "warning";
        break;
      case "cartCleared":
        title = "🧹 有人清空了購物車";
        text = "購物車已被清空";
        icon = "info";
        break;
    }

    Swal.fire({
      title,
      text,
      icon,
      timer: 3000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
      timerProgressBar: true,
      showCloseButton: true,
    });
  }

  // 顯示同步確認提示（由自己操作）
  showSyncNotification(data) {
    let title = "✅ 操作已同步";
    switch (data.type) {
      case "itemAdded":
        title = "✅ 商品已同步";
        break;
      case "itemUpdated":
        title = "✅ 數量已同步";
        break;
      case "itemRemoved":
        title = "✅ 移除已同步";
        break;
      case "cartCleared":
        title = "✅ 清空已同步";
        break;
    }

    Swal.fire({
      title,
      timer: 1000,
      showConfirmButton: false,
      toast: true,
      position: "bottom-end",
      background: "#f0f9ff",
      color: "#0369a1",
      timerProgressBar: false,
      showCloseButton: false,
    });
  }

  // 註冊新訂單的回呼函式
  registerNewOrderCallback(callback) {
    this.onNewOrderCallback = callback;
  }

  // 取消註冊回呼
  unregisterNewOrderCallback() {
    this.onNewOrderCallback = null;
  }

  // 停止連線
  stop() {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
      this.isConnected = false;
      this.hasJoinedStoreGroup = false;
      this.currentTableDraftId = null;
      this.unregisterTableDraftCallbacks();
    }

    if (this.storeGroupRetryTimer) {
      clearTimeout(this.storeGroupRetryTimer);
      this.storeGroupRetryTimer = null;
    }

    if (this.pingTimer) {
      clearInterval(this.pingTimer); // ✅ 清除 Ping 計時器
      this.pingTimer = null;
    }
  }

  // 取得當前連線狀態
  getConnectionState() {
    return this.connection ? this.connection.state : "Disconnected";
  }

  // 嘗試手動重連
  async reconnect() {
    if (this.connection) {
      try {
        await this.connection.start();
        this.isConnected = true;
      } catch {
        this.isConnected = false;
      }
    }
  }
}

const signalRService = new SignalRService();
export default signalRService;
